import { Icons } from '@/src/modules/components/Icons';
import type { InvitationStats, InvitationTab } from '@/src/types';
import { cn } from '@/src/infrastructure/lib/utils';

interface Props {
  stats: InvitationStats | null;
  tab: InvitationTab;
  onTabChange: (tab: InvitationTab) => void;
}

/**
 * Stats strip rendered at the top of the dashboard. Skeletons until the
 * `/invitations/stats` response arrives — we keep the strip visible so the
 * layout doesn't shift when data lands. Now also acts as the primary filter.
 */
export function InvitationStatsStrip({ stats, tab, onTabChange }: Props) {
  const cards: Array<{ id: InvitationTab; label: string; value: number | null; icon: keyof typeof Icons; tone: string }> = [
    { id: 'all', label: 'All', value: stats?.total ?? null, icon: 'Calendar', tone: 'text-primary' },
    { id: 'active', label: 'Active', value: stats?.active ?? null, icon: 'CheckCircle', tone: 'text-status-published' },
    { id: 'drafts', label: 'Drafts', value: stats?.drafts ?? null, icon: 'Edit3', tone: 'text-status-draft' },
    { id: 'inactive', label: 'Inactive', value: stats?.inactive ?? null, icon: 'Clock', tone: 'text-on-surface-variant' },
    { id: 'archived', label: 'Archived', value: stats?.archived ?? null, icon: 'Archive', tone: 'text-amber-600' },
    // Paid for but not published (e.g. the window closed after payment) — 2026-09-24.
    { id: 'pending_publish', label: 'Paid · not published', value: stats?.pendingPublish ?? null, icon: 'Subscription', tone: 'text-status-published' },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
      {cards.map((card) => {
        const IconComp = Icons[card.icon] ?? Icons.Calendar;
        const isActive = tab === card.id;
        return (
          <button
            key={card.label}
            onClick={() => onTabChange(card.id)}
            type="button"
            className={cn(
              "text-left rounded-2xl border p-4 flex items-center gap-3 shadow-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
              isActive 
                ? "bg-primary/5 border-primary shadow-primary/10" 
                : "bg-surface border-outline-variant/30 hover:border-primary/50 hover:shadow-md"
            )}
          >
            <div className={cn("p-2 rounded-xl transition-colors", isActive ? "bg-primary text-white" : `bg-surface-low ${card.tone}`)}>
              <IconComp size={18} />
            </div>
            <div>
              <div className={cn("text-xs font-medium uppercase tracking-wide transition-colors", isActive ? "text-primary font-bold" : "text-on-surface-variant")}>
                {card.label}
              </div>
              <div className={cn("text-2xl font-bold tabular-nums transition-colors", isActive ? "text-primary" : "text-on-surface")}>
                {card.value ?? '—'}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
